import { nextTick, ref, shallowRef, watchEffect } from 'vue'
import type { Ref } from 'vue'

export function useDetachedKeyboardNavigation<T>(
  htmlRef:
    | Ref<HTMLDivElement | null>
    | InstanceType<typeof HTMLDivElement>
    | null,
  ListItems: Ref<T[] | undefined>,
  onEnterPressed: (item: T) => void,
  options?: {
    /** The property name of the item that indicates if it is focusable.
     * @default { key: 'focusable', enabledValue: true }
     */
    focusableProperty?: {
      /** The property name of the item that indicates if it is focusable.
       * @default 'focusable'
       */
      key: keyof T
      /** The value of the property that indicates if it is focusable.
       * @default true
       */
      enabledValue: boolean
    }
    /** Whether to automatically scroll to the focused item.
     * @default true
     */
    autoScroll?: boolean
  }
) {
  const focusedIndex = ref(-1)
  const divRef = shallowRef<HTMLDivElement | null>(
    htmlRef && 'value' in htmlRef ? htmlRef.value : htmlRef
  )

  // Watch for changes to the ref if it's a Ref
  if (htmlRef && 'value' in htmlRef) {
    watchEffect(() => {
      divRef.value = htmlRef.value
    })
  }

  function isItemFocusable(index: number) {
    if (index < 0 || !ListItems.value || !ListItems.value[index]) return false
    const item = ListItems.value[index] as Record<string, unknown>
    const key = (options?.focusableProperty?.key ?? 'focusable') as string
    return item[key] === (options?.focusableProperty?.enabledValue ?? true)
  }

  function findNextFocusableItem() {
    if (!ListItems.value) return -1
    let nextIndex = focusedIndex.value + 1
    while (nextIndex < ListItems.value.length) {
      if (isItemFocusable(nextIndex)) return nextIndex
      nextIndex = nextIndex + 1
    }
    return -1
  }

  function findPreviousFocusableItem() {
    if (!ListItems.value) return -1
    let previousIndex = focusedIndex.value - 1
    while (previousIndex >= 0) {
      if (isItemFocusable(previousIndex)) return previousIndex
      previousIndex = previousIndex - 1
    }
    return -1
  }

  function findFirstFocusableItem() {
    if (!ListItems.value) return -1
    let index = 0
    while (index < ListItems.value.length) {
      if (isItemFocusable(index)) return index
      index = index + 1
    }
    return -1
  }

  function focusItem(index: number) {
    if (index < 0) return
    if (!ListItems.value) return
    focusedIndex.value = index
    if (options?.autoScroll ?? true) {
      nextTick(() => scrollIntoView(index))
    }
  }

  function focusFirstItem() {
    const firstIndex = findFirstFocusableItem()
    if (firstIndex >= 0) {
      focusItem(firstIndex)
    }
  }

  function focusNextItem() {
    const nextIndex = findNextFocusableItem()
    if (nextIndex >= 0) {
      focusItem(nextIndex)
    }
  }

  function focusPreviousItem() {
    const previousIndex = findPreviousFocusableItem()
    if (previousIndex >= 0) {
      focusItem(previousIndex)
    }
  }

  function resetFocus() {
    const firstIndex = findFirstFocusableItem()
    if (firstIndex >= 0) {
      focusItem(firstIndex)
    }
  }

  function removeFocus() {
    focusedIndex.value = -1
  }

  function addEventListeners() {
    document.addEventListener('keydown', handleKeydown)
  }

  function removeEventListeners() {
    document.removeEventListener('keydown', handleKeydown)
    removeFocus()
  }

  function scrollIntoView(index: number) {
    if (index >= 0 && divRef.value) {
      const element = divRef.value.children[index] as HTMLElement
      if (element) {
        element.scrollIntoView({
          behavior: 'instant',
          block: 'nearest',
          inline: 'nearest'
        })
      }
    }
  }

  function handleKeydown(event: KeyboardEvent) {
    // Ignore key repeat events (when key is held down)
    if (event.repeat) {
      return
    }

    if (event.key === 'ArrowDown') {
      event.preventDefault()
      focusNextItem()
    } else if (event.key === 'ArrowUp') {
      event.preventDefault()
      focusPreviousItem()
    } else if (event.key === 'Enter' && focusedIndex.value >= 0) {
      if (!ListItems.value) return
      event.preventDefault()
      const item = ListItems.value[focusedIndex.value]
      if (item) {
        onEnterPressed(item)
      }
    }
  }

  return {
    focusedIndex,
    focusNextItem,
    focusPreviousItem,
    focusItem,
    focusFirstItem,
    resetFocus,
    removeFocus,
    addEventListeners,
    removeEventListeners
  }
}
